import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { AppConfigContext } from '@config/AppConfig';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import {
  useBelongToCompaniesInMasterSchedule,
  useNominatedInMasterSchedule,
} from '@masterSchedule/masterSchedule.hooks';
import { routes } from '@routes/routes.models';
import { EDesignBriefPermission, ERolePermission } from '@userProfile/permission/permission.constants';
import {
  hasPermissionInArray,
  isPermittedAsSiteOperationUser,
  isPermittedAsSystemUser,
} from '@userProfile/permission/permission.services';
import { useContext } from 'react';
import { useNavigate } from 'react-router';
import { useProjectIsArchived } from '@config/config.hooks';
import { EDesignBriefStatus } from './designBrief.constants';
import { IDesignBrief } from './detail/designBriefDetailPage.models';

export const useProtectViewT2 = (masterSchedule: IMasterScheduleDetail | undefined) => {
  const navigate = useNavigate();
  const isAllowViewT2 = useAllowViewT2(masterSchedule);
  // TODO show the detail permission deny page
  if (!isAllowViewT2) navigate(routes.t2.path);
};

export const useAllowViewT2 = (masterSchedule: IMasterScheduleDetail | undefined) => {
  const appConfigs = useContext(AppConfigContext);
  const isDesigner = hasPermissionInArray(
    appConfigs.configs.myPermissions,
    ERolePermission.ALL_DESIGNERS,
    masterSchedule?.responsibleDesigner?.companyTypeId,
    masterSchedule?.responsibleDesigner?.organizationTypeId,
  );
  const { isBelongToDesignerCompany, isBelongToCheckerCompany, isBelongToSiteOperationsCompany } =
    useBelongToCompaniesInMasterSchedule(masterSchedule);
  return isBelongToDesignerCompany || isBelongToCheckerCompany || isBelongToSiteOperationsCompany || isDesigner;
};

export const useAllowModifyT2 = (masterSchedule: IMasterScheduleDetail | undefined) => {
  const { isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);
  const { isBelongToSiteOperationsCompany } = useBelongToCompaniesInMasterSchedule(masterSchedule);
  return (isSrp || isTwc || isTwcm) && isBelongToSiteOperationsCompany;
};

export const useAllowNewT2 = () => {
  const appConfigs = useContext(AppConfigContext);
  return isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.INITATE]);
};

export const useAllowNewT2AndIssueToTwc = (masterSchedule?: IMasterScheduleDetail) => {
  const appConfigs = useContext(AppConfigContext);
  const { isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);
  return (
    (isSrp || isTwc || isTwcm) &&
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.INITATE])
  );
};

export const useAllowIssueToTwd = (masterSchedule?: IMasterScheduleDetail) => {
  const appConfigs = useContext(AppConfigContext);
  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);

  return (
    isTwc &&
    (isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.ISSUE]) ||
      isPermittedAsSystemUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.ISSUE]))
  );
};
export const useAllowCreateRevision = (masterSchedule?: IMasterScheduleDetail) => {
  const { isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);
  return isSrp || isTwc || isTwcm;
};

export const useAllowT2Comment = (
  masterSchedule: IMasterScheduleDetail | undefined,
  designBrief: IDesignBrief | undefined,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isNominatedDesigner } = useNominatedInMasterSchedule(masterSchedule);
  const { isBelongToDesignerCompany } = useBelongToCompaniesInMasterSchedule(masterSchedule);

  if (designBrief?.status !== EDesignBriefStatus.FINISHED)
    return { canCreateComment: false, canViewDraftComment: false };
  if (!isNominatedDesigner) return { canCreateComment: false, canViewDraftComment: false };

  const canCreateComment = hasPermissionInArray(
    appConfigs.configs.myPermissions,
    EDesignBriefPermission.COMMENT,
    masterSchedule?.responsibleDesigner?.companyTypeId,
    masterSchedule?.responsibleDesigner?.organizationTypeId,
  );

  const canViewDraftComment = isBelongToDesignerCompany && canCreateComment;
  return { canCreateComment, canViewDraftComment };
};

export const useAllowT2Response = (
  masterSchedule: IMasterScheduleDetail | undefined,
  designBrief: IDesignBrief | undefined,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);

  if (designBrief?.status !== EDesignBriefStatus.FINISHED) return false;
  if (!isSrp && !isTwc && !isTwcm) return false;
  return (
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.RESPONSE]) ||
    isPermittedAsSystemUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.RESPONSE])
  );
};

export const useIsAllowUploadAttachmentOrSupplementaryFile = () => {
  const appConfigs = useContext(AppConfigContext);
  return (
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.UPLOAD_ATTACHMENT]) ||
    isPermittedAsSystemUser(appConfigs.configs.myPermissions, [EDesignBriefPermission.UPLOAD_ATTACHMENT])
  );
};

export const useT2Visibility = (
  masterSchedule: IMasterScheduleDetail | undefined,
  designBrief: IDesignBrief | undefined,
  mode: DetailPageAccessMode,
) => {
  const isProjectArchived = useProjectIsArchived();

  const isAllowModifyT2 = useAllowModifyT2(masterSchedule);
  const isAllowUploadAttachmentOrSupplementaryFile = useIsAllowUploadAttachmentOrSupplementaryFile();
  const isAllowNewT2AndIssueToTwc = useAllowNewT2AndIssueToTwc(masterSchedule);
  const isAllowIssueToTwd = useAllowIssueToTwd(masterSchedule);
  const isAllowCreateRevision = useAllowCreateRevision(masterSchedule);

  let isReadOnlyDetailTab =
    !isAllowModifyT2 || (masterSchedule?.isCancelled ?? false) || (masterSchedule?.isLocked ?? false);
  let isReadOnlyCommentTab = true;
  let modeSupplementaryInfo: DetailPageAccessMode = 'View';
  let modeAttachment: DetailPageAccessMode =
    isAllowModifyT2 && isAllowUploadAttachmentOrSupplementaryFile ? mode : 'View';
  const isSuperseded = !!designBrief?.nextRevisionDesignBriefId;

  if (designBrief?.status === EDesignBriefStatus.FINISHED) {
    isReadOnlyDetailTab = true;
    modeSupplementaryInfo = isAllowUploadAttachmentOrSupplementaryFile ? mode : 'View';
    modeAttachment = 'View';
    isReadOnlyCommentTab = false;
  }

  if (
    designBrief?.status === EDesignBriefStatus.CANCELLED ||
    isSuperseded ||
    masterSchedule?.isCancelled ||
    isProjectArchived ||
    masterSchedule?.isLocked
  ) {
    isReadOnlyDetailTab = true;
    modeSupplementaryInfo = 'View';
    modeAttachment = 'View';
    isReadOnlyCommentTab = true;
  }

  return {
    isAllowNewT2AndIssueToTwc,
    isAllowIssueToTwd,
    isAllowCreateRevision,
    isReadOnlyDetailTab,
    modeSupplementaryInfo,
    modeAttachment,
    isReadOnlyCommentTab,
  };
};
